import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Drawer, IconButton, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PillButton, sa } from '../../superAdminUi';
import {
  getUserProfile,
  updateUserProfile,
  adminUpdateUser,
  searchRoles,
} from '../../../services/api/identityUserService';
import adminProvisioningService from '../../../services/api/adminProvisioningService';
import schoolStaffService from '../../../services/api/schoolStaffService';
import { buildScopeAssignments, buildProvisioningData, formatLastLogin } from '../userFields';
import { buildRoleOptions, FALLBACK_ROLE_NAMES, SAFE_SPACE_ROLE_CODES } from '../roleCatalog';
import { accountStatusDisplay } from '../userLifecycle';
import {
  readProfile,
  grantedGroupIds,
  buildOrganisations,
  findCurrentOrganisation,
  planEdit,
  sameSites,
  describeSites,
  diffRoles,
  rolesAfterEdit,
  provisioningRoleIds,
  staffLinkedRoleCodes,
  withKeptRestrictions,
} from '../userEdit';
import { loadOrganisationDetails } from '../organisationLookup';
import AccessList from './AccessList';
import RoleChecklist from './RoleChecklist';
import EmailField from './EmailField';
import ReviewChanges from './ReviewChanges';
import SaveProgress from './SaveProgress';
import { fieldSx, hintSx, labelSx, noteSx, sectionTitleSx, tone } from './panelStyles';

const PANEL_WIDTH = 520;
// Save ignores clicks this soon after the review opens, so a double-click on Review can't save.
export const REVIEW_SETTLE_MS = 500;

const initialsOf = (first, last, email) =>
  ((first?.[0] || '') + (last?.[0] || '')).toUpperCase() || (email?.[0] || '?').toUpperCase();

const fullName = (first, last) => `${first || ''} ${last || ''}`.trim();

// Edit user — a side panel covering everything about one user: name, email, the organisations
// they can reach, and their roles. Changes are reviewed before they're saved, and saving runs as
// named steps (name → roles and schools → dashboards) so a failure says exactly what didn't happen
// and can be retried from there.
function EditUserPanel({
  open,
  userId,
  onClose,
  scope,
  schoolId,
  groupId,
  scopeName,
  siteOptions,
  onCompleted,
  onOpenOrganisation,
}) {
  const [phase, setPhase] = useState('loading'); // loading | loadError | ready
  const [loadError, setLoadError] = useState('');
  const [view, setView] = useState('edit'); // edit | review | saving
  const [profile, setProfile] = useState(null);
  const [details, setDetails] = useState(null);
  const [identityRoles, setIdentityRoles] = useState([]);
  const [form, setForm] = useState({ firstName: '', lastName: '' });
  const [sites, setSites] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [steps, setSteps] = useState([]);
  const runners = useRef({});
  // When the review opened: Save sits where Review was, so a double-click must not skip the review.
  const reviewOpenedAt = useRef(0);
  const [reloadToken, setReloadToken] = useState(0);

  // The section clears the user as the panel starts to close; keep showing them while it slides out.
  const lastUserId = useRef(userId);
  if (userId) lastUserId.current = userId;
  const shownUserId = userId || lastUserId.current;

  const siteIdsOfPage = useMemo(() => (siteOptions || []).map((s) => s.id), [siteOptions]);

  // Load the profile, then the organisations it reaches, then seed the form.
  useEffect(() => {
    if (!open || !userId) return undefined;
    let cancelled = false;
    setPhase('loading');
    setView('edit');
    setFieldErrors({});
    setConfirmDiscard(false);

    const known = { groups: {}, schoolNamesById: {} };
    (siteOptions || []).forEach((s) => {
      known.schoolNamesById[s.id] = s.name;
    });
    if (scope === 'group' && siteOptions?.length) {
      known.groups[Number(groupId)] = { name: scopeName || '', schools: siteOptions };
    }
    if (scope === 'school' && scopeName) known.schoolNamesById[Number(schoolId)] = scopeName;

    Promise.all([getUserProfile(userId), searchRoles().catch(() => [])])
      .then(async ([rawProfile, roles]) => {
        const read = readProfile(rawProfile);
        const loaded = await loadOrganisationDetails({
          groupIds: grantedGroupIds(read.scopes),
          schoolIds: read.scopes.filter((s) => s.type === 'School').flatMap((s) => s.entityIds),
          known,
        });
        if (cancelled) return;
        setProfile(read);
        setDetails(loaded);
        setIdentityRoles(Array.isArray(roles) ? roles : []);
        setForm({ firstName: read.firstName, lastName: read.lastName });
        setSelectedCodes(read.roleCodes);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.message || 'This user couldn’t be loaded.');
        setPhase('loadError');
      });
    return () => {
      cancelled = true;
    };
    // siteOptions/scopeName only seed names; re-running on their identity would discard edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, userId, reloadToken]);

  const { organisations, complete } = useMemo(
    () => (profile && details ? buildOrganisations(profile.scopes, details.groupSchoolIdsById) : { organisations: [], complete: true }),
    [profile, details]
  );
  const current = useMemo(
    () => findCurrentOrganisation(organisations, { scope, schoolId, groupId, groupSchoolIds: siteIdsOfPage }),
    [organisations, scope, schoolId, groupId, siteIdsOfPage]
  );
  const plan = useMemo(
    () => planEdit({ organisations, complete, current, scope, firstName: profile?.firstName }),
    [organisations, complete, current, scope, profile]
  );
  const target = plan.provisioningTarget;
  const schoolNamesById = details?.schoolNamesById || {};

  // Seed the school picker from the trust grant once the plan is known.
  const seededFor = useRef(null);
  useEffect(() => {
    if (phase !== 'ready' || seededFor.current === profile) return;
    seededFor.current = profile;
    const initial = target?.initialSiteIds || [];
    setSites(initial.map((id) => ({ id, name: schoolNamesById[id] || `School ${id}` })));
  }, [phase, profile, target, schoolNamesById]);

  const allSiteIds = useMemo(() => target?.allSiteIds || [], [target]);
  const initialSiteIds = target?.initialSiteIds || [];
  const pickerOptions = useMemo(
    () => allSiteIds.map((id) => ({ id, name: schoolNamesById[id] || `School ${id}` })),
    [allSiteIds, schoolNamesById]
  );
  const selectedSiteIds = plan.sitesEditable ? sites.map((s) => s.id) : initialSiteIds;
  const singleSchool = target?.scope === 'school' || selectedSiteIds.length === 1;

  // Every assignable role, with SafeSpace roles kept visible but unavailable outside one school.
  const roleOptions = useMemo(
    () =>
      buildRoleOptions(identityRoles, { singleSchool: true, fallbackNames: FALLBACK_ROLE_NAMES }).map((option) => ({
        ...option,
        unavailableReason:
          SAFE_SPACE_ROLE_CODES.includes(option.code) && !singleSchool
            ? 'Only for someone in a single school — choose one school above to change it.'
            : undefined,
      })),
    [identityRoles, singleSchool]
  );
  const heldCodes = useMemo(() => profile?.roleCodes || [], [profile]);
  const changeableCodes = plan.rolesEditable ? roleOptions.filter((o) => !o.unavailableReason).map((o) => o.code) : [];
  const catalogueCodes = roleOptions.map((o) => o.code);
  const otherRoles = heldCodes.filter((code) => !catalogueCodes.includes(code));
  const roleName = (code) =>
    roleOptions.find((o) => o.code === code)?.name || profile?.roleNamesByCode?.[code] || code;

  const { toAssign, toRemove } = diffRoles({ heldCodes, changeableCodes, selectedCodes });
  const finalRoles = rolesAfterEdit({ heldCodes, changeableCodes, selectedCodes });
  const nameChanged =
    profile !== null &&
    (form.firstName.trim() !== profile.firstName.trim() || form.lastName.trim() !== profile.lastName.trim());
  const rolesChanged = toAssign.length > 0 || toRemove.length > 0;
  const sitesChanged = plan.sitesEditable && !sameSites(initialSiteIds, selectedSiteIds, allSiteIds);
  const changeCount = (nameChanged ? 1 : 0) + toAssign.length + toRemove.length + (sitesChanged ? 1 : 0);
  const dirty = changeCount > 0;

  const namesFor = (org) =>
    org.kind === 'group'
      ? details?.groupNamesById?.[org.id] || (scope === 'group' && Number(groupId) === org.id && scopeName) || `Trust ${org.id}`
      : schoolNamesById[org.id] || `School ${org.id}`;

  const resetEdits = () => {
    if (!profile) return;
    setForm({ firstName: profile.firstName, lastName: profile.lastName });
    setSelectedCodes(profile.roleCodes);
    setSites(initialSiteIds.map((id) => ({ id, name: schoolNamesById[id] || `School ${id}` })));
    setFieldErrors({});
  };

  const requestClose = () => {
    if (view === 'saving' && steps.some((s) => s.status === 'running')) return;
    if (view !== 'saving' && dirty) {
      setConfirmDiscard(true);
      return;
    }
    seededFor.current = null;
    onClose();
  };

  const setField = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Enter a first name.';
    if (!form.lastName.trim()) errs.lastName = 'Enter a last name.';
    if (plan.rolesEditable && finalRoles.length === 0) errs.roles = 'Choose at least one role.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goToReview = () => {
    if (!validate()) return;
    reviewOpenedAt.current = Date.now();
    setView('review');
  };

  // --- Saving --------------------------------------------------------------------------------

  const linkStaffRoles = async (snapshot) => {
    const before = staffLinkedRoleCodes(heldCodes, snapshot.singleSchool);
    const after = staffLinkedRoleCodes(snapshot.finalRoles, snapshot.singleSchool);
    const changed = before.length !== after.length || after.some((code) => !before.includes(code));
    if (!snapshot.targetSchoolId || after.length === 0 || !changed) return;

    // Reuse the staff record Identity already links in that school; create one only if none.
    let staffId = profile.staffLinks.find((link) => link.schoolId === snapshot.targetSchoolId)?.staffId;
    if (!staffId) {
      const staffResponse = await schoolStaffService.addSchoolStaff({
        FirstName: snapshot.firstName,
        LastName: snapshot.lastName,
        Email: profile.email,
        SchoolId: snapshot.targetSchoolId,
        StartDate: '',
        EmploymentType: 1,
        CoreFilters: [{ JobTitle: 'Teaching' }],
        FilterValues: [],
      });
      staffId = staffResponse?.data?.Id ?? staffResponse?.Id ?? staffResponse?.id;
      if (!staffId) throw new Error('The staff record needed for this role couldn’t be created.');
    }
    await adminProvisioningService.upsertUserStaffRoles({
      staffId,
      schoolId: snapshot.targetSchoolId,
      userId,
      roles: after,
    });
  };

  const startSave = () => {
    if (Date.now() - reviewOpenedAt.current < REVIEW_SETTLE_MS) return;
    // Freeze what is being saved, so a retry repeats exactly the reviewed change.
    const snapshot = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      toAssign,
      toRemove,
      finalRoles,
      siteIds: selectedSiteIds,
      singleSchool,
      targetSchoolId: target?.scope === 'school' ? target.schoolId : selectedSiteIds.length === 1 ? selectedSiteIds[0] : null,
    };

    const planned = [];
    const handlers = {};
    if (nameChanged) {
      planned.push({ key: 'profile', label: 'Update name' });
      handlers.profile = () =>
        updateUserProfile(userId, { firstName: snapshot.firstName, lastName: snapshot.lastName });
    }
    if (rolesChanged || sitesChanged) {
      planned.push({
        key: 'access',
        label: rolesChanged && sitesChanged ? 'Update roles and schools' : rolesChanged ? 'Update roles' : 'Update schools',
      });
      handlers.access = () =>
        adminUpdateUser(userId, {
          roleCodesToAssign: snapshot.toAssign.length ? snapshot.toAssign : undefined,
          roleCodesToRemove: snapshot.toRemove.length ? snapshot.toRemove : undefined,
          scopeAssignments: sitesChanged
            ? withKeptRestrictions(
                buildScopeAssignments({ scope: 'group', groupId: target.groupId, siteIds: snapshot.siteIds, allSiteIds }),
                profile.scopes,
                { groupId: target.groupId, groupSchoolIds: allSiteIds }
              )
            : undefined,
        });
    }
    if (target) {
      planned.push({ key: 'dashboards', label: 'Update dashboards' });
      handlers.dashboards = async () => {
        await adminProvisioningService.provisionUser(
          buildProvisioningData({
            userId,
            email: profile.email,
            firstName: snapshot.firstName,
            lastName: snapshot.lastName,
            scope: target.scope,
            groupId: target.groupId,
            schoolId: target.schoolId,
            siteIds: snapshot.siteIds,
            allSiteIds,
            roleIds: provisioningRoleIds(snapshot.finalRoles),
          })
        );
        await linkStaffRoles(snapshot);
      };
    }

    runners.current = handlers;
    const initial = planned.map((step) => ({ ...step, status: 'pending' }));
    setSteps(initial);
    setView('saving');
    runFrom(initial, 0);
  };

  const runFrom = async (stepList, startIndex) => {
    let list = stepList.map((step, index) => (index >= startIndex ? { ...step, status: 'pending', error: undefined } : step));
    setSteps(list);
    let savedSomething = list.some((step) => step.status === 'done');
    for (let index = startIndex; index < list.length; index += 1) {
      list = list.map((step, i) => (i === index ? { ...step, status: 'running' } : step));
      setSteps(list);
      try {
        // eslint-disable-next-line no-await-in-loop
        await runners.current[list[index].key]();
        list = list.map((step, i) => (i === index ? { ...step, status: 'done' } : step));
        savedSomething = true;
        setSteps(list);
      } catch (err) {
        list = list.map((step, i) =>
          i === index ? { ...step, status: 'failed', error: err?.message || 'This step didn’t complete.' } : step
        );
        setSteps(list);
        break;
      }
    }
    if (savedSomething && onCompleted) onCompleted();
  };

  const retry = () => {
    const failedIndex = steps.findIndex((step) => step.status === 'failed');
    if (failedIndex >= 0) runFrom(steps, failedIndex);
  };

  const running = steps.some((step) => step.status === 'running');
  const failed = steps.some((step) => step.status === 'failed');

  // --- Render --------------------------------------------------------------------------------

  const status = profile ? accountStatusDisplay(profile.status) : null;
  const headerName = profile ? fullName(profile.firstName, profile.lastName) || profile.email : '';

  const review = {
    name: nameChanged
      ? { before: fullName(profile.firstName, profile.lastName), after: fullName(form.firstName.trim(), form.lastName.trim()) }
      : null,
    schools: sitesChanged
      ? { before: describeSites(initialSiteIds, schoolNamesById), after: describeSites(selectedSiteIds, schoolNamesById) }
      : null,
    roles: rolesChanged ? { added: toAssign.map(roleName), removed: toRemove.map(roleName) } : null,
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={requestClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: PANEL_WIDTH }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box
        component='header'
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: `1px solid ${sa.color.stroke}` }}
      >
        {profile && (
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: '#e0f2f1',
              color: sa.color.tealDark,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: sa.font.family,
              fontWeight: sa.font.semibold,
              fontSize: 14,
              flexShrink: 0,
            }}
            aria-hidden='true'
          >
            {initialsOf(profile.firstName, profile.lastName, profile.email)}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id='edit-user-title'
            sx={{ fontFamily: sa.font.family, fontSize: 17, fontWeight: sa.font.semibold, color: sa.color.text }}
            noWrap
          >
            {view === 'review' ? 'Review changes' : view === 'saving' ? 'Saving changes' : headerName || 'Edit user'}
          </Typography>
          {profile && view === 'edit' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
              <Box component='span' sx={{ fontFamily: sa.font.family, fontSize: 11.5, fontWeight: sa.font.semibold, px: 1, borderRadius: sa.radius.pill, background: status.bg, color: status.fg }}>
                {status.label}
              </Box>
              <Typography sx={{ ...hintSx, fontSize: 12 }}>
                {profile.lastLoginAt ? `Last signed in ${formatLastLogin(profile.lastLoginAt)}` : 'Never signed in'}
              </Typography>
            </Box>
          )}
          {profile && view !== 'edit' && <Typography sx={{ ...hintSx, fontSize: 12 }}>{headerName}</Typography>}
        </Box>
        <IconButton aria-label='Close' onClick={requestClose} disabled={running} size='small'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }} aria-labelledby='edit-user-title'>
        {phase === 'loading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
            <CircularProgress size={28} sx={{ color: sa.color.teal }} />
            <Typography sx={hintSx}>Loading user…</Typography>
          </Box>
        )}

        {phase === 'loadError' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={noteSx(tone.removed)} role='alert'>{loadError}</Box>
            <Box>
              <PillButton type='button' $variant='outline' onClick={() => setReloadToken((t) => t + 1)}>
                Try again
              </PillButton>
            </Box>
          </Box>
        )}

        {phase === 'ready' && view === 'edit' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Box component='section' aria-label='Profile'>
              <Typography sx={sectionTitleSx}>Profile</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography component='label' htmlFor='edit-user-first-name' sx={labelSx}>First name</Typography>
                    <TextField
                      id='edit-user-first-name'
                      value={form.firstName}
                      onChange={setField('firstName')}
                      fullWidth
                      size='small'
                      error={Boolean(fieldErrors.firstName)}
                      helperText={fieldErrors.firstName}
                      sx={fieldSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography component='label' htmlFor='edit-user-last-name' sx={labelSx}>Last name</Typography>
                    <TextField
                      id='edit-user-last-name'
                      value={form.lastName}
                      onChange={setField('lastName')}
                      fullWidth
                      size='small'
                      error={Boolean(fieldErrors.lastName)}
                      helperText={fieldErrors.lastName}
                      sx={fieldSx}
                    />
                  </Box>
                </Box>
                <EmailField userId={shownUserId} email={profile.email} />
              </Box>
            </Box>

            <Box component='section' aria-label='Access'>
              <Typography sx={sectionTitleSx}>Access</Typography>
              <AccessList
                organisations={organisations}
                current={current}
                scope={scope}
                namesFor={namesFor}
                schoolNamesById={schoolNamesById}
                sitesEditable={plan.sitesEditable}
                siteOptions={pickerOptions}
                sites={sites}
                onSitesChange={setSites}
                onOpen={onOpenOrganisation ? (org) => onOpenOrganisation(org, shownUserId) : undefined}
              />
              {!complete && (
                <Typography sx={{ ...hintSx, mt: 1 }}>Some organisation details couldn’t be loaded.</Typography>
              )}
            </Box>

            <Box component='section' aria-label='Roles'>
              <Typography sx={sectionTitleSx}>Roles</Typography>
              {plan.rolesEditable ? (
                <Typography sx={{ ...hintSx, mb: 1.25 }}>Roles apply everywhere this user has access.</Typography>
              ) : (
                <Box sx={{ ...noteSx(), mb: 1.25 }}>{plan.rolesLockedReason}</Box>
              )}
              <RoleChecklist
                options={roleOptions}
                heldCodes={heldCodes}
                selectedCodes={selectedCodes}
                onChange={(codes) => {
                  setSelectedCodes(codes);
                  setFieldErrors((prev) => ({ ...prev, roles: undefined }));
                }}
                readOnly={!plan.rolesEditable}
              />
              {fieldErrors.roles && (
                <Typography sx={{ ...hintSx, color: tone.removed.fg, mt: 0.75 }} role='alert'>{fieldErrors.roles}</Typography>
              )}
              {otherRoles.length > 0 && (
                <Typography sx={{ ...hintSx, mt: 1 }}>
                  Also holds {otherRoles.map(roleName).join(', ')}, which {otherRoles.length === 1 ? 'is' : 'are'} managed outside Access Management.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {phase === 'ready' && view === 'review' && (
          <ReviewChanges
            name={review.name}
            schools={review.schools}
            roles={review.roles}
            keepsRestrictions={sitesChanged}
            syncsDashboards={Boolean(target)}
          />
        )}

        {phase === 'ready' && view === 'saving' && <SaveProgress steps={steps} />}
      </Box>

      {phase === 'ready' && (
        <Box
          component='footer'
          sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.75, borderTop: `1px solid ${sa.color.stroke}`, background: sa.color.white }}
        >
          {view === 'edit' && confirmDiscard && (
            <>
              <Typography sx={{ ...hintSx, flex: 1, color: sa.color.text }}>Discard your unsaved changes?</Typography>
              <PillButton type='button' $variant='outline' onClick={() => setConfirmDiscard(false)}>Keep editing</PillButton>
              <PillButton
                type='button'
                $variant='danger'
                onClick={() => {
                  setConfirmDiscard(false);
                  resetEdits();
                  seededFor.current = null;
                  onClose();
                }}
              >
                Discard
              </PillButton>
            </>
          )}

          {view === 'edit' && !confirmDiscard && (
            <>
              <Typography sx={{ ...hintSx, flex: 1 }} aria-live='polite'>
                {dirty ? `${changeCount} unsaved change${changeCount === 1 ? '' : 's'}` : 'No changes yet'}
              </Typography>
              {dirty && (
                <PillButton type='button' $variant='outline' $size='lg' onClick={resetEdits}>Discard</PillButton>
              )}
              <PillButton type='button' $variant='teal' $size='lg' onClick={goToReview} disabled={!dirty}>
                Review changes
              </PillButton>
            </>
          )}

          {view === 'review' && (
            <>
              <Box sx={{ flex: 1 }} />
              <PillButton type='button' $variant='outline' $size='lg' onClick={() => setView('edit')}>Back</PillButton>
              <PillButton type='button' $variant='teal' $size='lg' onClick={startSave}>Save changes</PillButton>
            </>
          )}

          {view === 'saving' && !running && (
            <>
              <Box sx={{ flex: 1 }} />
              {failed ? (
                <>
                  <PillButton type='button' $variant='outline' $size='lg' onClick={onClose}>Close</PillButton>
                  <PillButton type='button' $variant='teal' $size='lg' onClick={retry}>Retry</PillButton>
                </>
              ) : (
                <PillButton type='button' $variant='teal' $size='lg' onClick={onClose}>Done</PillButton>
              )}
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
}

EditUserPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired,
  scope: PropTypes.oneOf(['school', 'group']).isRequired,
  schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  scopeName: PropTypes.string,
  siteOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
  onCompleted: PropTypes.func,
  // (organisation, userId) => void — opens that school/trust's page with this user's panel open.
  onOpenOrganisation: PropTypes.func,
};

EditUserPanel.defaultProps = {
  siteOptions: [],
};

export default EditUserPanel;
